import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useFolders, useDeleteFolder, useUpdateFolder, useSetFolderArchived } from '../../hooks/useFolders'
import { useFolderOrder } from '../../hooks/useFolderOrder'
import { Folder } from '../../types/models'
import { CreateFolderModal } from './CreateFolderModal'
import { FolderAclModal } from './FolderAclModal'
import { DraggableFolderItem } from './DraggableFolderItem'
import { AlertDialog } from '../common/AlertDialog'

// Notionライクなカラーパレット
const FOLDER_COLORS = [
  { name: 'デフォルト', value: '#3B82F6' },
  { name: 'グレー', value: '#6B7280' },
  { name: 'ブラウン', value: '#92400E' },
  { name: 'オレンジ', value: '#EA580C' },
  { name: '黄', value: '#CA8A04' },
  { name: '緑', value: '#16A34A' },
  { name: '青', value: '#2563EB' },
  { name: '紫', value: '#7C3AED' },
  { name: 'ピンク', value: '#DB2777' },
  { name: '赤', value: '#DC2626' },
]

interface FolderSidebarProps {
  selectedFolderId?: string | null
  onFolderSelect: (folderId: string | null) => void
}

export function FolderSidebar({ 
  selectedFolderId, 
  onFolderSelect
}: FolderSidebarProps) {
  const navigate = useNavigate()
  const { data: foldersData, error, isLoading, refetch } = useFolders()
  const deleteFolder = useDeleteFolder()
  const updateFolder = useUpdateFolder()
  const setFolderArchived = useSetFolderArchived()
  const { reorderFolders, updateFolderOrder, isUpdating } = useFolderOrder()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [alertDialog, setAlertDialog] = useState<{ isOpen: boolean; title?: string; message: string; type?: 'info' | 'error' | 'warning' | 'success' }>({
    isOpen: false,
    message: ''
  })
  
  // Notionライクな編集状態管理
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [showColorPicker, setShowColorPicker] = useState<string | false>(false)
  const [showMenu, setShowMenu] = useState<string | false>(false)
  const [showAclModal, setShowAclModal] = useState<{ folderId: string; folderName: string; acl?: any; visibility?: 'org' | 'restricted' } | null>(null)
  const colorPickerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  // アーカイブセクションの開閉（通常時は閉じておく）
  const [showArchivedSection, setShowArchivedSection] = useState(false)

  const originalFolders = foldersData?.folders || []
  const [folders, setFolders] = useState<Folder[]>([])
  
  // フォルダデータが更新されたらローカル状態も更新
  useEffect(() => {
    if (originalFolders.length > 0) {
      // 表示順序でソート（orderフィールドがある場合）
      const sortedFolders = [...originalFolders].sort((a, b) => {
        // アーカイブ済みは常に末尾（アクティブ一覧のDnDインデックスを保つ）
        const archA = a.archived ? 1 : 0
        const archB = b.archived ? 1 : 0
        if (archA !== archB) return archA - archB
        const orderA = (a as any).order || 0
        const orderB = (b as any).order || 0
        return orderA - orderB
      })
      setFolders(sortedFolders)
    }
  }, [originalFolders])

  const handleCreateFolder = () => {
    setShowCreateModal(true)
  }

  const handleFolderCreated = async () => {
    console.log('✅ Folder created, refreshing folder list...')
    setShowCreateModal(false)
    // フォルダリストを強制的に再取得
    await refetch()
    console.log('✅ Folder list refreshed')
  }

  // 外部クリックでドロップダウンを閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false)
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    if (showColorPicker || showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showColorPicker, showMenu])

  // 名前編集開始（メニューから）
  const handleStartEditNameFromMenu = (folder: Folder) => {
    setShowMenu(false)
    setEditingFolderId(folder.id)
    setEditingName(folder.name)
    setTimeout(() => nameInputRef.current?.focus(), 0)
  }

  // 名前編集開始（直接クリック - 削除予定）
  const handleStartEditName = (folder: Folder, event: React.MouseEvent) => {
    event.stopPropagation()
    setEditingFolderId(folder.id)
    setEditingName(folder.name)
    setTimeout(() => nameInputRef.current?.focus(), 0)
  }

  // 名前編集保存
  const handleSaveName = async (folderId: string) => {
    if (editingName.trim() === '') return
    
    try {
      await updateFolder.mutateAsync({
        id: folderId,
        name: editingName.trim()
      })
      setEditingFolderId(null)
      setEditingName('')
    } catch (error) {
      setAlertDialog({
        isOpen: true,
        title: 'エラー',
        message: `フォルダ名の更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
        type: 'error'
      })
    }
  }

  // カラー変更
  const handleToggleArchive = async (folder: Folder) => {
    setShowMenu(false)
    try {
      await setFolderArchived.mutateAsync({ id: folder.id, archived: !folder.archived })
    } catch (error) {
      setAlertDialog({
        isOpen: true,
        title: 'エラー',
        message: `アーカイブ状態の更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
        type: 'error'
      })
    }
  }

  const handleColorChange = async (folderId: string, color: string) => {
    try {
      await updateFolder.mutateAsync({
        id: folderId,
        color: color
      })
      setShowColorPicker(false)
    } catch (error) {
      setAlertDialog({
        isOpen: true,
        title: 'エラー',
        message: `カラーの更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
        type: 'error'
      })
    }
  }

  // Enterキーで保存、Escapeキーでキャンセル
  const handleKeyDown = (event: React.KeyboardEvent, folderId: string) => {
    if (event.key === 'Enter') {
      handleSaveName(folderId)
    } else if (event.key === 'Escape') {
      setEditingFolderId(null)
      setEditingName('')
    }
  }

  // メニューから削除実行
  const handleDeleteFromMenu = async (folder: Folder) => {
    setShowMenu(false)
    
    if (folder.documentCount > 0) {
      setAlertDialog({
        isOpen: true,
        title: '削除できません',
        message: `「${folder.name}」には${folder.documentCount}件の議事録が含まれています。先に議事録を移動または削除してください。`,
        type: 'warning'
      })
      return
    }

    if (confirm(`フォルダ「${folder.name}」を削除しますか？`)) {
      try {
        await deleteFolder.mutateAsync(folder.id)
        if (selectedFolderId === folder.id) {
          onFolderSelect(null)
        }
      } catch (error) {
        setAlertDialog({
          isOpen: true,
          title: 'エラー',
          message: `フォルダの削除に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
          type: 'error'
        })
      }
    }
  }

  // ドラッグアンドドロップハンドラー
  const handleMove = useCallback((dragIndex: number, hoverIndex: number) => {
    const newFolders = reorderFolders(folders, dragIndex, hoverIndex)
    setFolders(newFolders)
  }, [folders, reorderFolders])

  const handleDrop = useCallback(async (dragIndex: number, hoverIndex: number) => {
    console.log('🎯 handleDrop called:', { dragIndex, hoverIndex, foldersLength: folders.length })
    
    // インデックスが同じ場合は何もしない
    if (dragIndex === hoverIndex) {
      console.log('⏭️ Same position, skipping update')
      return
    }
    
    try {
      // すべてのフォルダの順序を更新
      console.log('📦 Updating all folder orders...')
      const updatePromises = folders.map((folder, index) => {
        console.log(`🔢 Folder ${folder.name}: order = ${index}`)
        return updateFolderOrder(folder.id, index)
      })
      
      await Promise.all(updatePromises)
      console.log('✅ All folder orders updated successfully')
    } catch (error) {
      console.error('❌ Failed to update folder order:', error)
      // エラー時は元の順序に戻す
      setFolders(originalFolders)
      setAlertDialog({
        isOpen: true,
        title: 'エラー',
        message: 'フォルダの順序更新に失敗しました',
        type: 'error'
      })
    }
  }, [folders, updateFolderOrder, originalFolders])

  const handleDeleteFolder = async (folder: Folder, event: React.MouseEvent) => {
    event.stopPropagation()
    
    if (folder.documentCount > 0) {
      setAlertDialog({
        isOpen: true,
        title: '削除できません',
        message: `「${folder.name}」には${folder.documentCount}件の議事録が含まれています。先に議事録を移動または削除してください。`,
        type: 'warning'
      })
      return
    }

    if (confirm(`フォルダ「${folder.name}」を削除しますか？`)) {
      try {
        await deleteFolder.mutateAsync(folder.id)
        if (selectedFolderId === folder.id) {
          onFolderSelect(null)
        }
      } catch (error) {
        setAlertDialog({
          isOpen: true,
          title: 'エラー',
          message: `フォルダの削除に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
          type: 'error'
        })
      }
    }
  }

  if (error) {
    return (
      <div className="flex flex-col">
        {/* ヘッダー */}
        <div className="nav-section">
          <div className="nav-label flex items-center justify-between">
            フォルダ
            <button
              onClick={handleCreateFolder}
              className="btn-icon sm borderless"
              title="新しいフォルダを作成"
            >
              <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          <button
            onClick={handleCreateFolder}
            className="btn btn-outline btn-sm w-full"
          >
            <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M12 4v16m8-8H4" />
            </svg>
            フォルダを追加
          </button>
        </div>

        {/* エラー表示 */}
        <div className="flex-1 p-4">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-4 text-danger" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-sm text-danger mb-2">
              フォルダの読み込みに失敗しました
            </p>
            <p className="text-xs text-tertiary">
              システムの準備中です。しばらくお待ちください。
            </p>
          </div>
        </div>

        {/* フォルダ作成モーダル */}
        {showCreateModal && (
          <CreateFolderModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleFolderCreated}
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* フォルダ一覧（メインナビの「議事録」の続きとして表示） */}
      <div className="flex-1 overflow-y-auto nav-section">
        {/* ブックマーク */}
        <div
          className={`nav-item cursor-pointer ${selectedFolderId === 'bookmarks' ? 'active' : ''}`}
          onClick={() => {
            navigate('/documents')
            onFolderSelect('bookmarks')
          }}
        >
          <svg style={{ color: 'var(--color-warning-mid)' }} fill="currentColor" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          <span className="text-sm font-medium">ブックマーク</span>
        </div>

        {/* フォルダラベル */}
        <div className="nav-label">フォルダ</div>

        {/* ローディング */}
        {isLoading && (
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-tertiary">
            <span className="spinner sm" aria-hidden="true" />
            読み込み中...
          </div>
        )}

        {/* フォルダリスト */}
        <DndProvider backend={HTML5Backend}>
          {folders.filter((f) => !f.archived).map((folder, index) => (
            <DraggableFolderItem
              key={folder.id}
              folder={folder}
              index={index}
              selectedFolderId={selectedFolderId}
              onFolderSelect={onFolderSelect}
              onMove={handleMove}
              onDrop={handleDrop}
            >
              {/* カラーアイコン（クリック可能） */}
              <div className="relative">
                <div 
                  className="w-3 h-3 mr-2 rounded-sm flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-gray-300 transition-all"
                  style={{ backgroundColor: folder.color || '#3B82F6' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowColorPicker(showColorPicker === folder.id ? false : folder.id)
                  }}
                />
                
                {/* カラーピッカードロップダウン */}
                {showColorPicker === folder.id && (
                  <div
                    ref={colorPickerRef}
                    className="dropdown-menu open"
                    style={{ top: '1.5rem', padding: 'var(--space-3)' }}
                  >
                    <div className="dropdown-menu-label" style={{ padding: 0, marginBottom: 'var(--space-2)' }}>カラー</div>
                    <div className="grid grid-cols-5 gap-2">
                      {FOLDER_COLORS.map((color) => (
                        <button
                          key={color.value}
                          className="w-6 h-6 rounded transition-all"
                          style={{ backgroundColor: color.value }}
                          onClick={() => handleColorChange(folder.id, color.value)}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* フォルダ情報（1行: 名前 + 件数） */}
              <div className="flex-1 min-w-0 flex items-center gap-2" title={folder.description || folder.name}>
                {editingFolderId === folder.id ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={() => handleSaveName(folder.id)}
                    onKeyDown={(e) => handleKeyDown(e, folder.id)}
                    className="form-input text-sm font-medium px-1 py-0.5 w-full"
                    style={{ padding: '2px 6px' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="text-sm truncate flex-1">{folder.name}</span>
                )}
                <span className="text-xs text-tertiary flex-shrink-0">{folder.documentCount}</span>
              </div>

              {/* 三点リーダーメニュー */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMenu(showMenu === folder.id ? false : folder.id)
                  }}
                  className="btn-icon sm borderless opacity-0 group-hover:opacity-100 transition-all"
                  title="フォルダメニュー"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>

                {/* ドロップダウンメニュー */}
                {showMenu === folder.id && (
                  <div
                    ref={menuRef}
                    className="dropdown-menu right open"
                    style={{ top: '2rem' }}
                  >
                    <button
                      onClick={() => handleStartEditNameFromMenu(folder)}
                      className="dropdown-item"
                    >
                      <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      名前を変更
                    </button>
                    <button
                      onClick={() => {
                        setShowMenu(false)
                        setShowAclModal({ folderId: folder.id, folderName: folder.name, acl: folder.acl, visibility: folder.visibility })
                      }}
                      className="dropdown-item"
                    >
                      <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      権限を管理
                    </button>
                    <button
                      onClick={() => handleToggleArchive(folder)}
                      className="dropdown-item"
                    >
                      <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
                      </svg>
                      アーカイブへ移動
                    </button>
                    <button
                      onClick={() => handleDeleteFromMenu(folder)}
                      className="dropdown-item danger"
                    >
                      <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      削除
                    </button>
                  </div>
                )}
              </div>
            </DraggableFolderItem>
          ))}
        </DndProvider>

        {/* アーカイブ済みフォルダ（通常時は折りたたみ） */}
        {folders.some((f) => f.archived) && (
          <div>
            <button
              className="nav-item w-full bg-transparent border-0 text-left"
              style={{ color: 'var(--color-text-tertiary)' }}
              onClick={() => setShowArchivedSection((v) => !v)}
            >
              <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ transform: showArchivedSection ? 'rotate(90deg)' : undefined, transition: 'transform .15s' }}>
                <path d="M9 18l6-6-6-6" />
              </svg>
              <span className="text-sm">アーカイブ</span>
              <span className="text-xs text-tertiary" style={{ marginLeft: 'auto' }}>
                {folders.filter((f) => f.archived).length}
              </span>
            </button>
            {showArchivedSection && folders.filter((f) => f.archived).map((folder) => (
              <div
                key={folder.id}
                className={`nav-item cursor-pointer group ${selectedFolderId === folder.id ? 'active' : ''}`}
                style={{ opacity: 0.7 }}
                onClick={() => onFolderSelect(folder.id)}
                title={folder.description || folder.name}
              >
                <div
                  className="w-3 h-3 mr-2 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: folder.color || '#9ca3af' }}
                />
                <span className="text-sm truncate flex-1">{folder.name}</span>
                <span className="text-xs text-tertiary flex-shrink-0">{folder.documentCount}</span>
                <button
                  className="btn-icon sm borderless opacity-0 group-hover:opacity-100 transition-all"
                  title="アーカイブから戻す"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleArchive(folder)
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M3 9l9-7 9 7M5 10v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* フォルダ追加ボタン */}
        <button
          onClick={handleCreateFolder}
          className="nav-item w-full bg-transparent border-0 text-left"
        >
          <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          フォルダを追加
        </button>

        {/* フォルダがない場合 */}
        {!isLoading && folders.length === 0 && (
          <div className="px-4 py-8 text-center">
            <svg className="w-12 h-12 mx-auto mb-4 text-tertiary" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <p className="text-sm text-secondary mb-3">
              フォルダがありません
            </p>
            <p className="text-sm text-tertiary">
              上のボタンから最初のフォルダを作成してください
            </p>
          </div>
        )}
      </div>

      {/* フォルダ作成モーダル */}
      {showCreateModal && (
        <CreateFolderModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleFolderCreated}
        />
      )}

      {/* フォルダACL管理モーダル */}
      {showAclModal && (
        <FolderAclModal
          folderId={showAclModal.folderId}
          folderName={showAclModal.folderName}
          currentAcl={showAclModal.acl}
          currentVisibility={showAclModal.visibility}
          onClose={() => setShowAclModal(null)}
          onSuccess={async () => {
            setShowAclModal(null)
            await refetch()
          }}
        />
      )}

      {/* アラートダイアログ */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
      />
    </div>
  )
}
